import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { DataTable, TableSearch } from '@vibrent/electryon';
import { getSurveys, deleteSurvey } from './api';
import DeleteModal from './components/deleteModal';
import './SurveyList.scss';
import DateUtil from '../common/dateUtil';
import { formatCellValue, getLocalizedText } from './textUtils';

const WIDTH_SIZE_1 = '12%';
const WIDTH_SIZE_2 = '6%';
const WIDTH_SIZE_3 = '8%';
const WIDTH_SIZE_4 = '3%';
const EDIT_BUTTON = {
  title: 'Edit',
  btnClass: 'right-btn-1 right-icon-edit no-background-btn',
  iconClass: 'fa fa-pen',
  iconColor: '#4a4a4a',
};
const DELETE_BUTTON = {
  title: 'Delete',
  btnClass: 'right-btn-1 right-icon-download no-background-btn',
  iconClass: 'fa fa-trash-alt',
  iconColor: '#4a4a4a',
};

const renderValue = value => (
  <div>
    <div>{formatCellValue(value)}</div>
  </div>
);

const renderSurveyIdLink = (cell, row) => {
  const surveyId = row && row.surveyId != null ? row.surveyId : cell;
  const id = getLocalizedText(surveyId);
  if (id == null) {
    return (
      <div>
        <div>N/A</div>
      </div>
    );
  }
  return (
    <Link to={`/surveys/version/${id}`}>
      {id}
    </Link>
  );
};

const renderActions = (row, buttons) => buttons.map((btn) => {
  const isVisible = btn.isVisible ? btn.isVisible(row) : true;
  if (!isVisible) return null;

  return (
    <button
      key={`${btn.title}-${row.id || row.uuid}`} // Use a unique name instead of index
      className={btn.btnClass}
      onClick={() => btn.onClick(row)}
    >
      <i
        className={btn.iconClass}
        style={{ color: btn.iconColor }}
      />
    </button>
  );
});

const renderDate = (row, key, currentActiveProgram) => {
  const dateString = row[key];
  if (!dateString) return 'N/A';
  const displayDate = DateUtil.formatDateTimeZone(dateString, 'MMM D, YYYY, hh:mm A', currentActiveProgram.timeZone);
  return (
    <div>
      <div>{displayDate} {currentActiveProgram.timeZone}</div>
    </div>
  );
};

const renderPublishedStatus = (row) => {
  const status = row.latestVersionDraft ? 'Draft' : 'Published';
  return (
    <div>
      <div>{status}</div>
    </div>
  );
};


const renderButtons = (row, buttons) => <div className="custom-actions">{renderActions(row, buttons)}</div>;

const getColumn = (buttonList, currentActiveProgram) => {
  const standardCols = [
    {
      key: 'surveyId',
      text: 'ID',
      isPrimaryKey: true,
      pinned: true,
      headerAlign: 'left',
      dataAlign: 'left',
      width: WIDTH_SIZE_4,
      colHeaderCss: 'survey-table-th',
      formatter: (cell, row) => renderSurveyIdLink(cell, row),
    },
    {
      key: 'name',
      text: 'SURVEY NAME',
      width: WIDTH_SIZE_1,
      dataSort: true,
      colCss: 'file-repository-col',
      colHeaderCss: 'survey-table-th',
      formatter: cell => renderValue(cell),
    },
    {
      key: 'latestVersionNumber',
      text: 'VERSION NO.',
      width: WIDTH_SIZE_2,
      colCss: 'file-repository-col',
      colHeaderCss: 'survey-table-th',
      formatter: cell => renderValue(cell),
    },
    {
      key: 'createdOn',
      text: 'CREATED ON',
      width: WIDTH_SIZE_3,
      dataSort: true,
      colCss: 'file-repository-col',
      colHeaderCss: 'survey-table-th',
      formatter: (cell, row) => renderDate(row, 'createdOn', currentActiveProgram),
    },
    {
      key: 'createdByName',
      text: 'CREATED BY',
      width: WIDTH_SIZE_3,
      dataSort: true,
      colCss: 'file-repository-col',
      colHeaderCss: 'survey-table-th',
      formatter: cell => renderValue(cell),
    },
    {
      key: 'updatedOn',
      text: 'LAST MODIFIED',
      width: WIDTH_SIZE_3,
      dataSort: true,
      colCss: 'file-repository-col',
      colHeaderCss: 'survey-table-th',
      formatter: (cell, row) => renderDate(row, 'updatedOn', currentActiveProgram),
    },
    {
      key: 'lastPublishedVersionNumber',
      text: 'PUBLISHED VERSION',
      width: WIDTH_SIZE_2,
      colCss: 'file-repository-col',
      colHeaderCss: 'survey-table-th',
      formatter: cell => renderValue(cell),
    },
    {
      key: 'publishedStatus',
      text: 'CURRENT VERSION',
      width: WIDTH_SIZE_3,
      colCss: 'file-repository-col',
      colHeaderCss: 'survey-table-th',
      formatter: (cell, row) => renderPublishedStatus(row),
    },
    {
      key: 'ACTIONS',
      text: 'Actions',
      width: WIDTH_SIZE_2,
      colCss: 'file-repository-col-actions',
      colHeaderCss: 'survey-table-th',
      formatter: (cell, row) => renderButtons(row, buttonList),
    },
  ];
  return standardCols;
};

function SurveyList(props) {
  const { surveys, totalSurveys, currentActiveProgram } = props;
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isShowDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeletionId, setSelectedDeletionId] = useState(null);

  const itemsPerPage = 20;

  const loadSurveys = async (search = searchTerm) => {
    try {
      await props.getSurveys(currentPage - 1, itemsPerPage, search);
    } catch (err) {
      console.error('Failed to load surveys:', err);
    }
  };

  useEffect(() => {
    loadSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleDelete = async () => {
    try {
      await deleteSurvey(selectedDeletionId);
      await props.getSurveys(currentPage - 1, itemsPerPage, searchTerm);
      setShowDeleteModal(false);
      setSelectedDeletionId(null);
    } catch (err) {
      console.error('Failed to delete survey:', err);
    }
  };

  const renderDeleteModal = () => (
    <DeleteModal
      key="delete-modal"
      className="deleteConfirm"
      show={isShowDeleteModal}
      setIsShowConfirmModal={setShowDeleteModal}
      handleSubmit={handleDelete}
    />
  );

  const onDeleteRecord = (row) => {
    setSelectedDeletionId(row.surveyId);
    setShowDeleteModal(true);
  };

  const defaultAction = [
    {
      ...EDIT_BUTTON,
      onClick: (row) => {
        window.open(`/surveys/creator?id=${row.surveyId}&programId=${currentActiveProgram.programId}`, '_blank');
      },
    },
    {
      ...DELETE_BUTTON,
      onClick: row => onDeleteRecord(row),
      isVisible: row => row.hasPublishedVersions === false,
    },
  ];

  const handleSearch = (searchQuery) => {
    setCurrentPage(1);
    setSearchTerm(searchQuery);
    loadSurveys(searchQuery);
  };


  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    loadSurveys('');
  };

  const onChange = (page) => {
    setCurrentPage(page);
  };

  const options = {
    noDataText: (
      <div className="no-data">
        <div className="no-data__icon">
          <i className="fas fa-badge-check" />
        </div>
        <div className="no-data__title">No surveys found yet</div>
      </div>
    ),
    nameUnitItemTable: 'My Table',
    page: currentPage,
    defaultSortName: 'createdOn',
    defaultSortOrder: 'desc',
  };

  return (
    <div>
      <div className="titles">
        Survey Builder
      </div>
      <div className="survey-list">
        <div className="header-actions">
          <div className="search-container">
            <div className="surveys-count">
              Total Surveys: ({totalSurveys})
            </div>
            <TableSearch
              placeholder="Search"
              inputValue={searchTerm}
              serverSearch={handleSearch}
            />
          </div>
          {/* navigation handled by window.open for v4 here */}
          <div className="actions-buttons">
            <button
              type="button"
              className="refresh-surveys-button"
              onClick={handleRefresh}
            >
              Refresh
            </button>
            <button
              type="button"
              className="create-surveys-button"
              onClick={() => {
                window.open(`/surveys/creator?programId=${currentActiveProgram.programId}`, '_blank');
              }}
            >
              Create
            </button>
          </div>
        </div>
        <React.Fragment>
          <div className="survey-table">
            <DataTable
              hover
              columns={getColumn(defaultAction, currentActiveProgram)}
              options={options}
              data={surveys}
              pagination
              serverPagination
              sizePerPage={itemsPerPage}
              serverTotalRecords={totalSurveys}
              serverPage={currentPage}
              onChangeCurrentPage={onChange}
            />
          </div>
        </React.Fragment>
        {renderDeleteModal()}
      </div>
    </div>
  );
}

SurveyList.propTypes = {
  getSurveys: PropTypes.func.isRequired,
  surveys: PropTypes.arrayOf(PropTypes.shape({})),
  totalSurveys: PropTypes.number,
  currentActiveProgram: PropTypes.shape({
    programId: PropTypes.number,
  }),
};

SurveyList.defaultProps = {
  getSurveys: PropTypes.func.isRequired,
  surveys: [],
  totalSurveys: 0,
  currentActiveProgram: null,
};

const mapStateToProps = state => ({
  surveys: state.SurveyBuilder.surveys,
  totalSurveys: state.SurveyBuilder.surveyCount,
  currentActiveProgram: state.UserDetail.get('currentActiveProgram'),
});

const mapDispatchToProps = dispatch => ({
  getSurveys: (page, perPage, search) => dispatch(getSurveys(page, perPage, search)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SurveyList);
